import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NavigationStart, Params, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';

import { AuthUser } from '../auth/auth.model';
import { AuthService } from '../auth/auth.service';
import { UserService } from '../user/user.service';

import { TranslationKeyMap } from './nav.i18n';
import { KeyboardNavigator } from './nav.keyboard';
import { NavItem, NavLoader } from './nav.model';
import { NavService } from './nav.service';

const profileRoutes = [['/profile'], ['/logout']];

@Component({
    selector: 'cmps-nav',
    templateUrl: './nav.component.html',
})
export class NavComponent implements OnInit, OnDestroy, NavLoader {
    i18n = TranslationKeyMap;
    private _isCollapsed: boolean;
    private _routerEventsSub: Subscription;
    nav: NavItem[];
    navLoaded: boolean;
    navError: boolean;
    photoUrl: SafeResourceUrl;

    /** @private */
    readonly _keyboardNavigator: KeyboardNavigator;

    /** @private */
    _navOpen: { [key: number]: boolean };

    get isCollapsed(): boolean {
        return this._isCollapsed;
    }

    set isCollapsed(value: boolean) {
        this._isCollapsed = value;
        // TODO add/remove class on window resize
        const className = 'cmps-navbar-fixed-overflow';
        if (!this._isCollapsed) {
            document.body.classList.add(className);
        } else {
            document.body.classList.remove(className);
        }
    }

    get user(): AuthUser {
        return this._authService.user;
    }

    get hasPhotoUrl(): boolean {
        return !!this.photoUrl;
    }

    constructor(
        private readonly _el: ElementRef,
        private readonly _router: Router,
        private readonly _domSanitizer: DomSanitizer,
        private readonly _navService: NavService,
        private readonly _userService: UserService,
        private readonly _authService: AuthService,
    ) {
        this.isCollapsed = true;
        _navService.register(this);

        this._keyboardNavigator = new KeyboardNavigator(_el.nativeElement);
        this._keyboardNavigator.toggleNavigation = ($event) =>
            this.onToggleNavigationClick($event);
        this._keyboardNavigator.openDropdown = (el) => {
            if (el?.hasAttribute('aria-haspopup')) {
                el.click();
            }
        };
        this._keyboardNavigator.closeDropdowns = () => {
            this._navOpen = {};
        };
        this._keyboardNavigator.aDropdownIsOpen = () => {
            return Object.values(this._navOpen).some((e) => e);
        };
        this._keyboardNavigator.isDropdown = (el) => {
            return el?.hasAttribute('aria-haspopup');
        };

        this._navOpen = {};
    }

    ngOnInit(): void {
        this._routerEventsSub = this._router.events
            .pipe(filter((event) => event instanceof NavigationStart))
            // eslint-disable-next-line no-return-assign
            .subscribe((event) => (this.isCollapsed = true));

        this.load(false)
            // eslint-disable-next-line n/handle-callback-err
            .subscribe(null, (error) => null);

        this.loadPhoto()
            // eslint-disable-next-line n/handle-callback-err
            .subscribe(null, (error) => null);
    }

    ngOnDestroy(): void {
        if (this._routerEventsSub) {
            this._routerEventsSub.unsubscribe();
        }

        this._keyboardNavigator.destroy();

        this._navService.reset();
        this._navService.register(null);
    }

    load(forceReload: boolean): Observable<NavItem[]> {
        this.nav = [];
        this.navLoaded = false;
        this.navError = false;

        return this._navService
            .list(this._authService.user.id, forceReload)
            .pipe(
                map((nav: NavItem[]) => {
                    this.nav = nav;
                    this.navLoaded = true;
                    return nav;
                }),
                catchError((error) => {
                    console.log(error);
                    this.navError = true;
                    throw error;
                }),
            );
    }

    loadPhoto(): Observable<string> {
        this.photoUrl = null;

        return this._userService.retrievePhoto(this.user.id).pipe(
            map((dataUri) => {
                this.photoUrl =
                    this._domSanitizer.bypassSecurityTrustUrl(dataUri);
                return dataUri;
            }),
        );
    }

    onToggleNavigationClick($event: MouseEvent | KeyboardEvent): void {
        $event.preventDefault();
        this.isCollapsed = !this.isCollapsed;
    }

    /**
     * @private
     *
     * workaround for routerLinkActive not picking up dynamic descendents. maybe a bit expensive
     */
    _childIsActive(item: NavItem): boolean {
        if (!item.isNested) {
            return item?.children?.some((e) => this._checkChildIsActive(e));
        } else {
            return item?.children?.some(
                (e) => e?.children?.some((ee) => this._checkChildIsActive(ee)),
            );
        }
    }

    /** @private */
    _profileChildIsActive(): boolean {
        return profileRoutes.some((e) => {
            return this._isActive(e, null);
        });
    }

    /** @private */
    _onItemClick($event: MouseEvent | KeyboardEvent, index: number): void {
        $event.preventDefault();
        $event.stopPropagation();
        const next = !this._navOpen[index];
        this._navOpen = {};
        this._navOpen[index] = next;
    }

    /** @private */
    _onDocumentClick(): void {
        this._navOpen = {};
    }

    private _checkChildIsActive(item: NavItem): boolean {
        const commands = Array.isArray(item?.link) ? item?.link : [item?.link];
        const queryParams = item?.queryParams;
        return this._isActive(commands, queryParams);
    }

    private _isActive(commands: any[], queryParams: Params): boolean {
        return this._router.isActive(
            this._router.createUrlTree(commands, { queryParams }),
            {
                paths: 'exact',
                queryParams: 'exact',
                fragment: 'ignored',
                matrixParams: 'ignored',
            },
        );
    }
}
